import React, { useCallback, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { useTranslation } from '@lib/useTypedTranslation';

import { List } from '../../../../../../components/list/list';
import { InitialTableState, useTableReducer } from '../../../../../../components/data-table/lib';
import { useWorldRequest } from '../../../../../../lib/useWorldRequest';
import { useWorldSettingsContext } from '../../../../../../context/worldSettings';
import { useUserSettingsContext } from '../../../../../../context/userSettings';
import { getDeviceAnomalyDataUsageApps, IDeviceAnomalyDataUsageAppItem } from '../../../../../../services/core/deviceAnomaliesDataUsageApps';
import { AnomalySectionProps } from './index';
import { useDataUsageAppsColumns } from './useDataUsageAppsColumns';

import './dataUsageAppsSection.css';

const tableName = 'deviceAnomalyDataUsageApps';
const defaultPageSize = 20;

export function DataUsageAppsSection(props: AnomalySectionProps) {
  const { deviceId, selectedDate } = props;
  const { t } = useTranslation('anomalyDetectionBeta');
  const { worldSettings } = useWorldSettingsContext();
  const { tablePageSizes } = useUserSettingsContext();

  const anomalyTz = worldSettings.smartGroupingTimeZone || 'UTC';

  const selectedDateUtc = useMemo(() => {
    const selectedDay = moment.utc(selectedDate).format('YYYY-MM-DD');
    return {
      from: moment.tz(selectedDay, anomalyTz).startOf('day').valueOf(),
      to: moment.tz(selectedDay, anomalyTz).endOf('day').valueOf()
    };
  }, [selectedDate, anomalyTz]);

  // Unlike processHangsSection.tsx's per-row Timestamp column (rendered in the device's own local
  // time), this table shows whole-day app totals with no per-row time component, so the title
  // just names the single anomalyTz day being examined rather than spelling out a device-local range.
  const title = t('DATA_USAGE_APPS_FOR_DAY', { date: moment.utc(selectedDate).format('Do MMMM YYYY') });

  const initialTableState: InitialTableState = {
    limit: tablePageSizes?.[tableName] || defaultPageSize,
    sort: { column: 4, direction: 'desc', field: 'total' }
  };
  const [tableReducerProperties, tableReducerFunctions] = useTableReducer<IDeviceAnomalyDataUsageAppItem>(tableName, initialTableState);
  const { sort } = tableReducerProperties;
  const { onLoading, onDataLoaded } = tableReducerFunctions;

  useEffect(() => {
    onLoading();
  }, [selectedDateUtc, onLoading]);

  const listFetcher = useCallback(() => {
    return getDeviceAnomalyDataUsageApps({ deviceId, from: selectedDateUtc.from, to: selectedDateUtc.to, timeZone: anomalyTz, sort: { field: sort.field, order: sort.direction } });
  }, [deviceId, selectedDateUtc, anomalyTz, sort.field, sort.direction]);

  const handleListFetched = useCallback((items: IDeviceAnomalyDataUsageAppItem[]) => {
    onDataLoaded(items, items.length, tableReducerProperties.dataRequestId);
  }, [onDataLoaded, tableReducerProperties.dataRequestId]);

  const columns = useDataUsageAppsColumns();

  return (
    <div data-id="device-anomaly-examination-data-usage-apps">
      <List<IDeviceAnomalyDataUsageAppItem, IDeviceAnomalyDataUsageAppItem[]>
        dataId="device-anomaly-examination-data-usage-apps-list"
        customHeader={false}
        hasCheckboxes={false}
        hasFilters={false}
        hasSearch={false}
        translationItems={{
          statusText: { key: 'RESULT', ns: 'translation' },
          tableTitle: { text: title }
        }}
        columns={columns}
        fetcher={listFetcher}
        onListFetched={handleListFetched}
        tableReducerFunctions={tableReducerFunctions}
        tableReducerProperties={tableReducerProperties}
        useRequestHook={useWorldRequest}
        options={{ getKey: (item) => item.packageName }}
      />
    </div>
  );
}
